/**
 * Owner reports of a quiz (HomeReportsPanel.cs):
 *  - Ranking: every player's record sorted by points, with a correct / wrong
 *    bar over the number of questions (PrefabRankingHome.cs).
 *  - Statistics ("accuracy"): per question, how many got it right and how the
 *    answers were distributed (each option with its percentage, or, for
 *    free-text questions, the distinct answers given).
 * Reads projects/{id}/answers (optionally only one classroom's).
 * Games played in a classroom: GameResultsReport (projects/{id}/gameResults).
 * A quiz someone shared with me (HomeReportsNotMineSubpanel): MyAnswersReport,
 * what this browser played, and RankingReport with `played`, the ranking
 * everyone sees at the end of the quiz (without classroom records; only my row
 * when the owner hides the ranking), with my row marked.
 */
import { useEffect, useMemo, useState } from "react";
import type { Project, Slide } from "@makeit/schema";
import type { MyAnswers } from "../data/answers";
import { fetchAnswers, type AnswerRecord } from "../data/myProjects";
import { clockTime, fetchGameResults, summarize, type GameResult } from "../data/gameResults";
import { currentLanguage, t } from "../player/i18n";
import { slideTypeName } from "./slideNames";

const OPS: Record<string, string> = { sum: "+", subtraction: "-", multiplication: "x", division: "÷" };

/** Question text of a slide as the reports show it. */
export function questionText(s: Slide): string {
  switch (s.type) {
    case "quiz":
    case "quizzesQuiz":
    case "trueFalse":
    case "typeAnswer":
    case "fillWithLetters":
      return s.question?.text.content ?? "";
    case "math": {
      const parts = [String(s.element1), OPS[s.operation] ?? "?", String(s.element2), "=", s.toGuess === "result" ? "?" : String(s.result)];
      if (s.toGuess === "element1") parts[0] = "?";
      if (s.toGuess === "element2") parts[2] = "?";
      if (s.toGuess === "operation") parts[1] = "?";
      return parts.join(" ");
    }
    case "missingLetter": {
      const w = s.word?.text.content ?? "";
      return Array.from(w)
        .map((c, i) => (i === s.missingLetter ? "_" : c))
        .join("");
    }
    case "missingWord": {
      const words = (s.phrase?.text.content ?? "").split(/[\n ]+/).filter(Boolean);
      return words.map((w, i) => (i === s.missingWord ? "____" : w)).join(" ");
    }
    case "word":
      return `{ ${(s.word?.text.content ?? "").toUpperCase()} }`;
    case "phrase":
      return `{ ${s.phrase?.text.content ?? ""} }`;
    default:
      return "";
  }
}

/** Fixed answer options of a slide (text + correct), or null for free-text questions. */
function optionsOf(s: Slide): { text: string; correct: boolean }[] | null {
  switch (s.type) {
    case "quiz":
    case "quizzesQuiz":
    case "trueFalse":
      return s.quizElements.map((q) => ({ text: q.text.content, correct: q.correct }));
    case "math":
    case "missingLetter":
    case "missingWord":
      return s.answers.map((q) => ({ text: q.text.content, correct: q.correct }));
    default:
      return null;
  }
}

export const isQuestion = (s: Slide) => s.type !== "free";

export function useAnswers(projectId: string, classroomId?: string) {
  const [state, setState] = useState<{ list: AnswerRecord[] | null; error: boolean }>({ list: null, error: false });
  useEffect(() => {
    let gone = false;
    setState({ list: null, error: false });
    fetchAnswers(projectId, classroomId)
      .then((list) => !gone && setState({ list, error: false }))
      .catch(() => !gone && setState({ list: [], error: true }));
    return () => {
      gone = true;
    };
  }, [projectId, classroomId]);
  return state;
}

export function RankingReport({ project, classroomId, played }: { project: Project; classroomId?: string; played?: MyAnswers }) {
  const answers = useAnswers(project.id, classroomId);
  const error = answers.error;
  const list = useMemo(() => {
    if (!played || !answers.list) return answers.list;
    const open = answers.list.filter((a) => a.classroomId === null);
    return project.settings.showRanking ? open : open.filter((a) => a.id === played.id);
  }, [answers.list, played, project]);
  const totalQuestions = useMemo(() => {
    let n = project.slides.filter(isQuestion).length;
    for (const a of list ?? []) n = Math.max(n, a.answers.length);
    return Math.max(1, n);
  }, [project, list]);
  if (error) return <div className="empty muted">{t("errServer")}</div>;
  if (list === null) return <div className="empty muted">…</div>;
  if (list.length === 0) return <div className="empty muted">{t("noAnswersYet")}</div>;
  return (
    <div className="rank-list">
      {list.map((a, i) => {
        const correct = a.answers.filter((x) => x.correct).length;
        const wrong = a.answers.length - correct;
        return (
          <div key={a.id} className={`rank-row${played && a.id === played.id ? " mine" : ""}`}>
            <span className="rank-num">{i + 1}</span>
            <span className="rank-name" dir="auto">
              {a.userName}
              {!a.finished && <span className="muted"> · {t("inProgress")}</span>}
            </span>
            <span className="rank-bar" title={`${correct} ✓ · ${wrong} ✗ / ${totalQuestions}`}>
              <span className="ok" style={{ width: `${(100 * correct) / totalQuestions}%` }} />
              <span className="ko" style={{ width: `${(100 * wrong) / totalQuestions}%` }} />
            </span>
            <span className="rank-points">{a.totalPoints}</span>
          </div>
        );
      })}
    </div>
  );
}

/** A quiz someone shared with me: the name and points played in this browser, and each question with the answer given, right, wrong or not answered. */
export function MyAnswersReport({ project, played }: { project: Project; played: MyAnswers }) {
  const lang = currentLanguage();
  return (
    <div className="my-answers">
      <p className="my-answers-head">
        <span className="muted">{t("classStudentNameInfo")}</span>
        <strong dir="auto">{played.userName}</strong>
        {!played.finished && <span className="muted">· {t("inProgress")}</span>}
        <span className="my-answers-points">
          {played.totalPoints} {t("points")}
        </span>
      </p>
      <h3>{t("myAnswers")}</h3>
      <div className="rank-list">
        {project.slides.filter(isQuestion).map((s, i) => {
          const given = played.answers.find((a) => a.slideId === s.id);
          const state = given ? (given.correct ? "ok" : "ko") : "none";
          return (
            <div key={s.id} className={`my-answer ${state}`}>
              <span className="rank-num">{i + 1}</span>
              <span className="my-answer-q">
                <span dir="auto">{questionText(s) || slideTypeName(s.type, lang)}</span>
                <span className="my-answer-a" dir="auto">
                  {given?.answer || "—"}
                </span>
              </span>
              <span className="my-answer-mark">{given ? (given.correct ? "✓" : "✗") : "–"}</span>
              <span className="rank-points">{given?.points ?? 0}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function StatsReport({ project, classroomId }: { project: Project; classroomId?: string }) {
  const { list, error } = useAnswers(project.id, classroomId);
  if (error) return <div className="empty muted">{t("errServer")}</div>;
  if (list === null) return <div className="empty muted">…</div>;
  if (list.length === 0) return <div className="empty muted">{t("noAnswersYet")}</div>;
  let n = 0;
  return (
    <div className="stats-list">
      {project.slides.map((s) => {
        n++;
        if (!isQuestion(s)) return null;
        // One answer per player and slide (the app guards against duplicates too).
        const given = list.map((a) => a.answers.find((x) => x.slideId === s.id)).filter((x): x is NonNullable<typeof x> => !!x);
        if (given.length === 0) return null;
        const correct = given.filter((x) => x.correct).length;
        const wrong = given.length - correct;
        const options = optionsOf(s);
        const rows = options
          ? options.map((o) => ({ text: o.text, correct: o.correct, count: given.filter((x) => x.answer === o.text).length }))
          : Object.values(
              given.reduce<Record<string, { text: string; correct: boolean; count: number }>>((acc, x) => {
                const k = x.answer.trim().toLowerCase();
                acc[k] = acc[k] ? { ...acc[k], count: acc[k].count + 1 } : { text: x.answer, correct: x.correct, count: 1 };
                return acc;
              }, {}),
            ).sort((a, b) => b.count - a.count);
        return (
          <div key={s.id} className="stat-q">
            <div className="stat-head">
              <span className="stat-num">{n}</span>
              <span className="stat-text" dir="auto">
                {questionText(s) || "—"}
              </span>
              <span className="stat-pct">
                {Math.round((100 * correct) / given.length)}% <span className="muted">({given.length})</span>
              </span>
            </div>
            <span className="rank-bar">
              <span className="ok" style={{ width: `${(100 * correct) / given.length}%` }} />
              <span className="ko" style={{ width: `${(100 * wrong) / given.length}%` }} />
            </span>
            <ul className="stat-answers">
              {rows.map((r, i) => (
                <li key={i} className={r.correct ? "correct" : ""}>
                  <span dir="auto">{r.text || "—"}</span>
                  <span className="muted">
                    {Math.round((100 * r.count) / given.length)}% · {r.count}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Classroom game results (ProjectClassroomsShare + SharedUsersPrefab.FillMe)
// ---------------------------------------------------------------------------

/**
 * One row per student of the classroom: games started, games finished, best
 * score and best time; a student's row opens to show each game (date,
 * finished or the pages reached, score, time).
 */
export function GameResultsReport({ projectId, classroomId, students }: { projectId: string; classroomId: string; students: { id: string; name: string }[] }) {
  const [state, setState] = useState<{ list: GameResult[] | null; error: boolean }>({ list: null, error: false });
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  useEffect(() => {
    let gone = false;
    setState({ list: null, error: false });
    fetchGameResults(projectId, classroomId)
      .then((list) => !gone && setState({ list, error: false }))
      .catch(() => !gone && setState({ list: [], error: true }));
    return () => {
      gone = true;
    };
  }, [projectId, classroomId]);
  const { list, error } = state;
  if (error) return <div className="empty muted">{t("errServer")}</div>;
  if (list === null) return <div className="empty muted">…</div>;
  if (students.length === 0) return <div className="empty muted">{t("classNoStudents")}</div>;
  const byStudent = new Map<string, GameResult[]>();
  for (const r of list) byStudent.set(r.studentId, [...(byStudent.get(r.studentId) ?? []), r]);
  const toggle = (id: string) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const dateFmt = new Intl.DateTimeFormat(document.documentElement.lang || undefined, { dateStyle: "short" });
  return (
    <div className="games-report">
      <div className="gr-row gr-head">
        <span />
        <span>{t("classGames")}</span>
        <span>{t("classFinished")}</span>
        <span>{t("classPuntuation")}</span>
        <span>{t("classTime")}</span>
      </div>
      {[...students]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((s) => {
          const mine = byStudent.get(s.id) ?? [];
          const sum = summarize(mine);
          const isOpen = open.has(s.id) && mine.length > 0;
          return (
            <div key={s.id} className={`gr-student ${isOpen ? "open" : ""}`}>
              <button className="gr-row gr-main" onClick={() => mine.length && toggle(s.id)} disabled={!mine.length} aria-expanded={isOpen}>
                <span className="gr-name" dir="auto">
                  {mine.length > 0 && <span className="gr-caret">{isOpen ? "▾" : "▸"}</span>}
                  {s.name}
                </span>
                <span>{mine.length ? sum.played : "-"}</span>
                <span>{sum.finished ? sum.finished : "-"}</span>
                <span className="gr-score">{sum.finished ? sum.best : "-"}</span>
                <span>{mine.length ? clockTime(sum.time) : "-"}</span>
              </button>
              {isOpen &&
                [...mine].reverse().map((r) => (
                  <div key={r.id} className="gr-row gr-game">
                    <span className="muted">{r.date ? dateFmt.format(r.date) : ""}</span>
                    <span />
                    <span className={r.finished ? "gr-ok" : "gr-ko"}>{r.finished ? "✓" : `✗ ${r.pages === 1 ? t("pagesCountOne") : `${r.pages} ${t("classSlides")}`}`}</span>
                    <span>{r.finished ? r.puntuation : "-"}</span>
                    <span>{clockTime(r.time)}</span>
                  </div>
                ))}
            </div>
          );
        })}
      <div className="gr-notes muted small">
        <p>{t("classInfoScore")}</p>
        <p>{t("classInfoHeaders")}</p>
        <p>{t("classInfoNotFinished")}</p>
      </div>
    </div>
  );
}
